import React from "react";
import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import {
  HiOutlineHeart,
  HiOutlineGlobe,
  HiOutlineTrophy,
  HiOutlineBriefcase,
  HiOutlineAcademicCap,
  HiOutlineUsers,
  HiOutlineSparkles,
  HiOutlineStar,
  HiOutlineLightBulb
} from "react-icons/hi";

// import Header from "../Header/Header";

const AboutUs = () => {
  // Refs for scroll animations
  const introRef = useRef(null);
  const historyRef = useRef(null);
  const missionRef = useRef(null);

  const isIntroInView = useInView(introRef, { once: true, amount: 0.3 });
  const isHistoryInView = useInView(historyRef, { once: true, amount: 0.3 });
  const isMissionInView = useInView(missionRef, { once: true, amount: 0.3 });

  // Animation variants
  const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" }
    }
  };

  const fadeInLeft = {
    hidden: { opacity: 0, x: -50 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.6, ease: "easeOut" }
    }
  };

  const fadeInRight = {
    hidden: { opacity: 0, x: 50 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.6, ease: "easeOut" }
    }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const scaleOnHover = {
    hover: {
      scale: 1.05,
      transition: { duration: 0.2 }
    }
  };

  return (
    <div className="w-full bg-gradient-to-br from-gray-50 via-white to-gray-50 min-h-screen overflow-hidden">
      {/* Animated Background Elements - Light Gray */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <motion.div
          className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-r from-gray-200 to-gray-300 rounded-full mix-blend-multiply filter blur-3xl opacity-40"
          animate={{
            x: [0, 50, 0],
            y: [0, -30, 0],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            repeatType: "reverse"
          }}
        />
        <motion.div
          className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-r from-gray-200 to-gray-300 rounded-full mix-blend-multiply filter blur-3xl opacity-40"
          animate={{
            x: [0, -50, 0],
            y: [0, 30, 0],
          }}
          transition={{
            duration: 18,
            repeat: Infinity,
            repeatType: "reverse"
          }}
        />
      </div>

      {/* Hero Section */}
      <div className="relative w-full h-[50vh] sm:h-[60vh] md:h-[70vh] lg:h-96 overflow-hidden">
        {/* Background Image with Parallax Effect */}
        <motion.div
          initial={{ scale: 1.2 }}
          animate={{ scale: 1 }}
          transition={{ duration: 1.5 }}
          className="absolute inset-0"
        >
          <img
            src="assets/Committee.png"
            alt="Event Background"
            className="w-full h-full object-cover"
          />
        </motion.div>

        {/* Light Gradient Overlay - Gray */}
        <div className="absolute inset-0 bg-gradient-to-t from-gray-200/70 via-gray-100/50 to-transparent" />

        {/* Floating Elements - Light Gray */}
        <motion.div
          className="absolute top-20 left-10 text-gray-300/40 hidden lg:block"
          animate={{
            y: [0, -20, 0],
            rotate: [0, 10, -10, 0]
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            repeatType: "reverse"
          }}
        >
          <HiOutlineStar size={60} />
        </motion.div>

        <motion.div
          className="absolute bottom-20 right-10 text-gray-300/40 hidden lg:block"
          animate={{
            y: [0, 20, 0],
            rotate: [0, -10, 10, 0]
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            repeatType: "reverse"
          }}
        >
          <HiOutlineSparkles size={50} />
        </motion.div>

        {/* Hero Content - Dark text for contrast */}
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4 sm:px-6">
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-3 sm:mb-4 text-gray-800"
          >
            About Us
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="text-sm sm:text-base md:text-lg lg:text-xl max-w-2xl px-4 leading-relaxed text-gray-700"
          >
            Explore inspiring stories, expert tips, and engaging insights to help embrace new experiences from our vibrant community
          </motion.p>

          {/* Scroll Indicator - Light Gray */}
          <motion.div
            className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            <div className="w-6 h-10 border-2 border-gray-400 rounded-full flex justify-center">
              <div className="w-1 h-2 bg-gray-400 rounded-full mt-2" />
            </div>
          </motion.div>
        </div>
      </div>

      {/* Introduction Section */}
      <motion.div
        ref={introRef}
        initial="hidden"
        animate={isIntroInView ? "visible" : "hidden"}
        variants={staggerContainer}
        className="flex flex-col lg:flex-row items-center px-4 sm:px-6 md:px-12 py-12 md:py-16 gap-6 md:gap-8 max-w-7xl mx-auto"
      >
        <motion.div variants={fadeInLeft} className="w-full lg:w-1/2">
          <div className="flex items-center space-x-2 mb-4">
            <motion.div
              whileHover={{ rotate: 360 }}
              transition={{ duration: 0.6 }}
              className="w-10 h-10 bg-gradient-to-r from-gray-100 to-gray-200 rounded-xl flex items-center justify-center border border-gray-200"
            >
              <HiOutlineHeart className="w-6 h-6 text-gray-600" />
            </motion.div>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-700">
              Introduction
            </h2>
          </div>

          <motion.p
            variants={fadeInUp}
            className="text-base sm:text-lg text-gray-600 leading-relaxed mb-6"
          >
            Learn about our journey, our values, and how we strive to create a thriving community for alumni and students alike.
          </motion.p>

          <motion.h3
            variants={fadeInUp}
            className="text-xl sm:text-2xl font-bold text-gray-700 mb-4"
          >
            Our Purpose and Mission
          </motion.h3>

          <motion.ul
            variants={staggerContainer}
            className="space-y-3"
          >
            {[
              { icon: "🤝", text: "Strengthen bonds between alumni and the institution" },
              { icon: "💼", text: "Provide networking, career growth, mentorship and opportunities" },
              { icon: "🎓", text: "Support the institution through scholarships and knowledge sharing" },
              { icon: "🎉", text: "Organize events and initiatives to keep alumni engaged" }
            ].map((item, index) => (
              <motion.li
                key={index}
                variants={fadeInUp}
                whileHover={{ scale: 1.02, x: 5 }}
                className="flex items-start bg-white rounded-xl p-4 shadow-md hover:shadow-lg transition-all duration-300 border border-gray-200"
              >
                <motion.span
                  className="text-2xl mr-3"
                  whileHover={{ rotate: 360 }}
                  transition={{ duration: 0.6 }}
                >
                  {item.icon}
                </motion.span>
                <span className="text-sm sm:text-base text-gray-600">{item.text}</span>
              </motion.li>
            ))}
          </motion.ul>
        </motion.div>

        <motion.div
          variants={fadeInRight}
          className="w-full lg:w-1/2 flex justify-center mt-6 lg:mt-0"
        >
          <motion.div
            whileHover={{ scale: 1.05 }}
            transition={{ duration: 0.3 }}
            className="relative group"
          >
            <div className="absolute -inset-2 bg-gradient-to-r from-gray-200 to-gray-300 rounded-3xl opacity-30 blur-xl group-hover:opacity-50 transition duration-300" />
            <img
              src="assets/imagew.png"
              alt="Introduction"
              className="relative w-full max-w-sm sm:max-w-md lg:max-w-lg h-auto rounded-2xl shadow-2xl"
            />
          </motion.div>
        </motion.div>
      </motion.div>

      {/* History and Legacy Section */}
      <motion.div
        ref={historyRef}
        initial="hidden"
        animate={isHistoryInView ? "visible" : "hidden"}
        variants={staggerContainer}
        className="flex flex-col lg:flex-row items-center px-4 sm:px-6 md:px-12 py-12 md:py-16 gap-6 md:gap-8 max-w-7xl mx-auto"
      >
        <motion.div
          variants={fadeInLeft}
          className="w-full lg:w-1/2 flex justify-center order-2 lg:order-1"
        >
          <motion.div
            whileHover={{ scale: 1.05 }}
            transition={{ duration: 0.3 }}
            className="relative group"
          >
            <div className="absolute -inset-2 bg-gradient-to-r from-gray-200 to-gray-300 rounded-3xl opacity-30 blur-xl group-hover:opacity-50 transition duration-300" />
            <img
              src="assets/imagew.png"
              alt="History and Legacy"
              className="relative w-full max-w-sm sm:max-w-md lg:max-w-lg h-auto rounded-2xl shadow-2xl"
            />
          </motion.div>
        </motion.div>

        <motion.div
          variants={fadeInRight}
          className="w-full lg:w-1/2 order-1 lg:order-2"
        >
          <div className="flex items-center space-x-2 mb-4">
            <motion.div
              whileHover={{ rotate: 360 }}
              transition={{ duration: 0.6 }}
              className="w-10 h-10 bg-gradient-to-r from-gray-100 to-gray-200 rounded-xl flex items-center justify-center border border-gray-200"
            >
              <HiOutlineGlobe className="w-6 h-6 text-gray-600" />
            </motion.div>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-700">
              History and Legacy
            </h2>
          </div>

          <motion.p
            variants={fadeInUp}
            className="text-base sm:text-lg text-gray-600 leading-relaxed mb-6"
          >
            Our institution has a rich legacy of academic excellence, fostering a strong network of alumni who have made significant contributions across various fields and industries.
          </motion.p>

          <motion.div
            variants={staggerContainer}
            className="space-y-4"
          >
            {[
              { icon: "✨", text: "Decades of Academic Excellence" },
              { icon: "🌍", text: "Global Network of Alumni" },
              { icon: "🏆", text: "Award-Winning Professionals" }
            ].map((item, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                whileHover={{ scale: 1.02, x: 5 }}
                className="flex items-center gap-3 p-4 bg-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300 border border-gray-200"
              >
                <motion.span
                  className="text-2xl"
                  whileHover={{ rotate: 360 }}
                  transition={{ duration: 0.6 }}
                >
                  {item.icon}
                </motion.span>
                <p className="text-sm sm:text-base text-gray-600">{item.text}</p>
              </motion.div>
            ))}
          </motion.div>

          {/* Legacy Stats - Light Gray */}
          <motion.div
            variants={fadeInUp}
            className="grid grid-cols-3 gap-3 mt-8"
          >
            {[
              { number: "50+", label: "Years" },
              { number: "10k+", label: "Alumni" },
              { number: "100+", label: "Awards" }
            ].map((stat, index) => (
              <motion.div
                key={index}
                whileHover={{ scale: 1.05 }}
                className="text-center p-3 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl border border-gray-200"
              >
                <div className="text-xl sm:text-2xl font-bold text-gray-700">{stat.number}</div>
                <div className="text-xs sm:text-sm text-gray-500">{stat.label}</div>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>
      </motion.div>

      {/* Mission & Vision Section */}
      <motion.div
        ref={missionRef}
        initial="hidden"
        animate={isMissionInView ? "visible" : "hidden"}
        variants={staggerContainer}
        className="flex flex-col lg:flex-row items-center px-4 sm:px-6 md:px-12 py-12 md:py-16 gap-6 md:gap-8 max-w-7xl mx-auto"
      >
        <motion.div variants={fadeInLeft} className="w-full lg:w-1/2">
          <div className="flex items-center space-x-2 mb-4">
            <motion.div
              whileHover={{ rotate: 360 }}
              transition={{ duration: 0.6 }}
              className="w-10 h-10 bg-gradient-to-r from-gray-100 to-gray-200 rounded-xl flex items-center justify-center border border-gray-200"
            >
              <HiOutlineLightBulb className="w-6 h-6 text-gray-600" />
            </motion.div>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-700">
              Mission & Vision
            </h2>
          </div>

          <motion.p
            variants={fadeInUp}
            className="text-base sm:text-lg text-gray-600 leading-relaxed mb-6"
          >
            We are committed to creating a thriving community for alumni and students, fostering lifelong connections and mutual growth.
          </motion.p>

          <motion.h3
            variants={fadeInUp}
            className="text-xl sm:text-2xl font-bold text-gray-700 mb-4"
          >
            Key Objectives
          </motion.h3>

          <motion.ul
            variants={staggerContainer}
            className="space-y-3"
          >
            {[
              { text: "Build lasting relationships between alumni and the institution" },
              { text: "Create career advancement and mentorship opportunities" },
              { text: "Support institutional growth and student scholarships" },
              { text: "Maintain active engagement through events and initiatives" }
            ].map((item, index) => (
              <motion.li
                key={index}
                variants={fadeInUp}
                whileHover={{ scale: 1.02, x: 5 }}
                className="flex items-start bg-gray-50 rounded-xl p-4 border-l-4 border-gray-300 shadow-md hover:shadow-lg transition-all duration-300"
              >
                <motion.span
                  className="text-xl mr-3 text-gray-500"
                  whileHover={{ scale: 1.2 }}
                >
                  ✓
                </motion.span>
                <span className="text-sm sm:text-base text-gray-600">{item.text}</span>
              </motion.li>
            ))}
          </motion.ul>
        </motion.div>

        <motion.div
          variants={fadeInRight}
          className="w-full lg:w-1/2 flex justify-center mt-6 lg:mt-0"
        >
          <motion.div
            whileHover={{ scale: 1.05 }}
            transition={{ duration: 0.3 }}
            className="relative group"
          >
            <div className="absolute -inset-2 bg-gradient-to-r from-gray-200 to-gray-300 rounded-3xl opacity-30 blur-xl group-hover:opacity-50 transition duration-300" />
            <img
              src="assets/imagew.png"
              alt="Mission"
              className="relative w-full max-w-sm sm:max-w-md lg:max-w-lg h-auto rounded-2xl shadow-2xl"
            />
          </motion.div>
        </motion.div>
      </motion.div>

      {/* Call to Action Section - Light Gray Gradient */}
      <motion.div
        initial={{ opacity: 0, y: 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true }}
        className="text-center px-4 sm:px-6 py-12 md:py-16 bg-gradient-to-r from-gray-100 via-gray-200 to-gray-100 mt-8"
      >
        <motion.h2
          className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-700 mb-4"
          whileHover={{ scale: 1.05 }}
        >
          Join Our Growing Community
        </motion.h2>
        <motion.p
          className="text-sm sm:text-base md:text-lg text-gray-600 mb-6 max-w-2xl mx-auto"
          whileHover={{ scale: 1.02 }}
        >
          Be part of something bigger. Connect with alumni, share experiences, and grow together.
        </motion.p>
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className="px-6 sm:px-8 py-3 bg-white text-gray-700 font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 text-sm sm:text-base border border-gray-200"
        >
          Get Started Today
        </motion.button>
      </motion.div>
    </div>
  );
};

export default AboutUs;